/**
 * RGM Starter Studio · Phase 1 + Phase 1B Recording Engine
 *
 * Wraps Web Audio API + MediaRecorder for a mobile-first, iOS-safe
 * multitrack vocal capture over a beat.
 *
 * Phase 1B additions:
 *   • `ensureReady()` — idempotent init + AudioContext resume from
 *     any user gesture (fixes the "engine not ready" iPhone error
 *     when the artist taps Record without first hitting Play).
 *   • `setMonitor(on)` — optional headphone monitoring branch
 *     (mic → gain → destination) that is TOTALLY decoupled from the
 *     recorded blob AND from the mic-capture processing options. It
 *     does NOT require echoCancellation to be enabled.
 *   • `pickRecorderMime()` prefers explicit codec-tagged strings so
 *     Safari and Chrome produce filenames whose base MIME matches
 *     the backend allow-list after the codec parameter is stripped.
 */

// ─── Feature detection ──────────────────────────────────────────────
import { buildFxChain, ensureGate, fxActive, gateOnly } from "./studioFx";
import { buildMasterChain } from "./studioMaster";
import { Metronome } from "./studioMetronome";

// Elapsed recording time from the AudioContext clock (ms).
export const recorderClockMs = (ctx, recorderStartCtx, startedAtCtx) => {
  const start = recorderStartCtx ?? startedAtCtx;
  if (!ctx || typeof start !== "number") return 0;
  return Math.max(0, Math.round((ctx.currentTime - start) * 1000));
};

// Playback-only repair for takes saved with a seconds-scaled duration
// (bytes per ms far above any real recording bitrate). Stored records
// are never modified; the real length comes from the audio file.
export const isSuspectDuration = (k) =>
  !!k && k.duration_ms > 0 && k.size_bytes > 20000 && k.size_bytes / k.duration_ms > 60;

export const probeMediaDurationMs = (url, timeoutMs = 8000) => new Promise((resolve) => {
  const el = new Audio();
  el.preload = "metadata";
  el.crossOrigin = "anonymous";
  const done = (v) => { clearTimeout(t); el.src = ""; resolve(v); };
  const t = setTimeout(() => done(0), timeoutMs);
  el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? Math.round(el.duration * 1000) : 0);
  el.onerror = () => done(0);
  el.src = url;
});

export const repairTakeDuration = (k, realMs) => {
  if (!realMs) return k;
  const out = { ...k, duration_ms: realMs, duration_repaired: true };
  if (k.clip_end_ms && k.clip_end_ms === k.duration_ms) out.clip_end_ms = realMs;
  return out;
};

export const CANDIDATE_MIMES = [
  // Safari 14.5+ returns true for both. Explicit codecs first so the
  // recorder blob carries a fully-qualified type — the backend
  // normalises the ';codecs=…' suffix before validating.
  'audio/mp4;codecs="mp4a.40.2"',
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
];

export function pickRecorderMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of CANDIDATE_MIMES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* older browsers throw — treat as unsupported */
    }
  }
  return "";
}

export function normalizeMime(m) {
  if (!m) return "";
  return m.split(";")[0].trim().toLowerCase();
}

export function studioSupport() {
  const hasMR = typeof MediaRecorder !== "undefined";
  const hasGUM = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  const hasAC = !!(window.AudioContext || window.webkitAudioContext);
  const mime = pickRecorderMime();
  return {
    supported: hasMR && hasGUM && hasAC && mime !== null,
    hasMediaRecorder: hasMR,
    hasGetUserMedia: hasGUM,
    hasAudioContext: hasAC,
    mime,
  };
}

// Convenient extension helper for filenames.
export function extForMime(mime) {
  const base = normalizeMime(mime);
  if (base === "audio/mp4" || base === "audio/aac" || base === "audio/x-m4a" || base === "audio/m4a") return "m4a";
  if (base === "audio/webm") return "webm";
  if (base === "audio/ogg") return "ogg";
  if (base === "audio/mpeg" || base === "audio/mp3") return "mp3";
  if (base === "audio/wav" || base === "audio/x-wav" || base === "audio/wave") return "wav";
  return "bin";
}

// ─── Wake Lock (feature-detected, best-effort) ──────────────────────
export async function requestWakeLock() {
  try {
    if ("wakeLock" in navigator && navigator.wakeLock?.request) {
      return await navigator.wakeLock.request("screen");
    }
  } catch {
    /* ignored — Wake Lock is a nice-to-have */
  }
  return null;
}

// ─── AudioContext unlock ────────────────────────────────────────────
export async function unlockAudio() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) throw new Error("Web Audio API not supported on this browser");
  const ctx = new Ctx();
  if (ctx.state === "suspended") {
    try { await ctx.resume(); } catch { /* iOS gesture required */ }
  }
  return ctx;
}

// ─── Mic capture with hardened iOS fallback ─────────────────────────
// `lowLatency` (headphone monitoring) skips the browser's echo-cancel /
// noise-suppress / auto-gain DSP, which is the main software delay.
export async function openMic(lowLatency = false) {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Microphone API not available. Use a modern browser.");
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: lowLatency
        ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1, latency: 0 }
        : { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      video: false,
    });
  } catch (e1) {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (e2) {
      const err = e2 || e1;
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        throw new Error("Microphone permission denied. Enable it in browser settings.");
      }
      if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        throw new Error("No microphone found on this device.");
      }
      throw new Error(`Microphone unavailable (${name || "unknown"}).`);
    }
  }
}

// ─── Audio helpers (pure, unit-tested) ──────────────────────────────
export const PEAK_RATE_STORE = 20;   // peaks/sec saved with a take
export const PEAK_RATE_VIEW = 100;   // peaks/sec rendered from decoded audio

const isIOS = () => typeof navigator !== "undefined" && (
  /iPad|iPhone|iPod/.test(navigator.userAgent || "")
  || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
);

// iOS 16.4+: leave voice-processing mode when the mic is not in use.
export function setAudioSession(type) {
  try { if (navigator.audioSession) navigator.audioSession.type = type; } catch { /* */ }
}

export function decodeAudio(ctx, arrayBuffer) {
  return new Promise((resolve, reject) => {
    try {
      const p = ctx.decodeAudioData(arrayBuffer, resolve, reject);
      if (p && typeof p.then === "function") p.then(resolve, reject);
    } catch (e) { reject(e); }
  });
}

// Cloudinary can transcode any stored audio to MP3 on the fly — used
// when the browser cannot decode the original container (iOS m4a).
export function cloudinaryMp3Url(url) {
  if (!url || !/^https:\/\/res\.cloudinary\.com\/[^/]+\/video\/upload\//i.test(url)) return null;
  return /\.[a-z0-9]{2,4}$/i.test(url) ? url.replace(/\.[a-z0-9]{2,4}$/i, ".mp3") : `${url}.mp3`;
}

// Absolute peak per bucket (0..255) across all channels.
export function bufferPeaks(buffer, rate = PEAK_RATE_VIEW) {
  if (!buffer) return [];
  const per = Math.max(1, Math.floor(buffer.sampleRate / rate));
  const n = Math.ceil(buffer.length / per);
  const out = new Array(n).fill(0);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const ch = buffer.getChannelData(c);
    for (let i = 0; i < n; i++) {
      let mx = 0;
      const to = Math.min(ch.length, (i + 1) * per);
      for (let j = i * per; j < to; j++) { const v = ch[j] < 0 ? -ch[j] : ch[j]; if (v > mx) mx = v; }
      const b = Math.min(255, Math.round(mx * 255));
      if (b > out[i]) out[i] = b;
    }
  }
  return out;
}

// Smooth (equal-power) fade shape, 0..1 → 0..1.
export const fadeShape = (x) => Math.sin(Math.max(0, Math.min(1, x)) * Math.PI / 2);

// Clip envelope (gain × fades) at clip-local time u (ms) of a window `win`.
export function clipEnvelopeAt(u, win, fin, fout) {
  const [fi, fo] = clampFades(win, fin, fout);
  let g = 1;
  if (fi > 0 && u < fi) g *= fadeShape(u / fi);
  if (fo > 0 && u > win - fo) g *= fadeShape((win - u) / fo);
  return g;
}

export function clampFades(win, fin = 0, fout = 0) {
  let fi = Math.max(0, fin || 0);
  let fo = Math.max(0, fout || 0);
  if (fi + fo > win && fi + fo > 0) { const k = win / (fi + fo); fi *= k; fo *= k; }
  return [fi, fo];
}

// AudioParam events for a clip that starts playing at clip-local u0 (ms).
// Times are seconds relative to the moment the clip becomes audible.
export function clipEnvelopeEvents(u0, win, fin, fout, points = 32) {
  const [fi, fo] = clampFades(win, fin, fout);
  const events = [];
  const sample = (a, b) => {
    const vals = [];
    for (let i = 0; i < points; i++) vals.push(clipEnvelopeAt(a + ((b - a) * i) / (points - 1), win, fi, fo));
    return vals;
  };
  let cursor = u0;
  if (fi > 0 && u0 < fi) {
    events.push({ type: "curve", t: 0, d: (fi - u0) / 1000, values: sample(u0, fi) });
    cursor = fi;
  } else {
    events.push({ type: "set", t: 0, v: clipEnvelopeAt(u0, win, fi, fo) });
  }
  const outStart = Math.max(cursor, win - fo);
  if (fo > 0 && outStart < win) {
    events.push({ type: "curve", t: (outStart - u0) / 1000 + 0.0005, d: (win - outStart) / 1000, values: sample(outStart, win) });
  }
  return events;
}

// Timeline → playback schedule for one clip. All values in ms.
// Returns null when the clip has already finished at `posMs`.
export function clipSchedule(posMs, timelineStart, srcStart, srcEnd) {
  const win = Math.max(0, srcEnd - srcStart);
  const tlEnd = timelineStart + win;
  if (win <= 0 || posMs >= tlEnd) return null;
  const u0 = Math.max(0, posMs - timelineStart);
  return {
    delayMs: Math.max(0, timelineStart - posMs),
    srcOffsetMs: srcStart + u0,
    durMs: win - u0,
    u0,
    win,
  };
}

// ─── The engine ─────────────────────────────────────────────────────
export class StudioEngine {
  constructor(beatUrl) {
    this.beatUrl = beatUrl;
    this.audioCtx = null;
    this.beatBuffer = null;
    this.beatEl = null;
    this.beatGain = null;
    this.master = null;
    this.micStream = null;
    this.micSource = null;
    this.monitorGain = null;
    this.monitor = false;
    this._monitorVol = 0.8;
    this._masterVol = 1;
    this._metroCfg = { on: false, bpm: 90 };
    this._metro = null;
    this.recorder = null;
    this.recordedChunks = [];
    this._buffers = new Map();
    this._buses = new Map();
    this._voices = [];
    this._wakeLock = null;
    this._onStateChange = null;
    this._initPromise = null;
    this._playing = false;
    this._posSec = 0;
    this.state = "idle"; // idle | ready | playing | recording | stopped
  }

  onStateChange(fn) { this._onStateChange = fn; }
  _setState(s) {
    this.state = s;
    if (this._onStateChange) this._onStateChange(s);
  }

  _ctx() {
    if (!this.audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) throw new Error("Web Audio API not supported on this browser");
      this.audioCtx = new Ctx({ latencyHint: "interactive" });
      this._buildGraph();
      ensureGate(this.audioCtx).then((ok) => {
        if (!ok) return;
        for (const b of this._buses.values()) b.fxKey = null;
        if (this._mixTracks) this.setTrackMix(this._mixTracks);
      });
    }
    return this.audioCtx;
  }

  _buildGraph() {
    const ctx = this.audioCtx;
    this.master = buildMasterChain(ctx);
    this.master.apply(this._masterPreset || "off", this._masterIntensity ?? 50);
    this.master.setVolume(this._masterVol ?? 1);
    this.beatGain = ctx.createGain();
    this.beatGain.gain.value = this._beatVol ?? 1;
    this.beatGain.connect(this.master.input);
    this._buses = new Map();
    if (this._mixTracks) this.setTrackMix(this._mixTracks);
  }

  // Decode once, cache. Falls back to a Cloudinary MP3 rendition.
  loadBuffer(url) {
    if (!url) return Promise.resolve(null);
    if (!this._buffers.has(url)) {
      const ctx = this._ctx();
      const attempt = async (u) => {
        const res = await fetch(u, { mode: "cors" });
        if (!res.ok) throw new Error(`http_${res.status}`);
        return decodeAudio(ctx, await res.arrayBuffer());
      };
      this._buffers.set(url, attempt(url).catch(() => {
        const alt = cloudinaryMp3Url(url);
        return alt && alt !== url ? attempt(alt).catch(() => null) : null;
      }));
    }
    return this._buffers.get(url);
  }

  cacheBuffer(url, buffer) { if (url && buffer) this._buffers.set(url, Promise.resolve(buffer)); }

  async init() {
    if (!this.beatUrl) throw new Error("no beat attached — pick a beat first");
    const ctx = this._ctx();
    if (ctx.state === "suspended") { try { await ctx.resume(); } catch { /* gesture */ } }
    this.beatBuffer = await this.loadBuffer(this.beatUrl);
    if (!this.beatBuffer) {
      // Element fallback only when the beat cannot be decoded at all.
      const el = new Audio();
      el.crossOrigin = "anonymous";
      el.preload = "auto";
      el.src = this.beatUrl;
      await new Promise((res, rej) => {
        el.addEventListener("loadedmetadata", res, { once: true });
        el.addEventListener("error", () => rej(new Error("beat failed to load")), { once: true });
      });
      this.beatEl = el;
      this.beatSource = ctx.createMediaElementSource(el);
      this.beatSource.connect(this.beatGain);
    }
    this._ready = true;
    this._setState("ready");
  }

  // Called synchronously from a Play / Record tap so a context created
  // here starts inside the user gesture (iOS requirement).
  async ensureReady() {
    if (this._needsRebuild) { this._needsRebuild = false; this._rebuildContextSync(); }
    if (this._ready && this.audioCtx) {
      await this._resume();
      return;
    }
    if (this.audioCtx && this.audioCtx.state !== "running") this._resume();
    if (!this._initPromise) {
      this._initPromise = (async () => {
        try { await this.init(); }
        finally { this._initPromise = null; }
      })();
    }
    await this._initPromise;
  }

  // resume() can hang on iOS ("interrupted" after the mic session ends);
  // never block the transport longer than 1.2 s — rebuild instead.
  async _resume() {
    const ctx = this.audioCtx;
    if (!ctx || ctx.state === "running") return;
    try { await Promise.race([ctx.resume(), sleep(1200)]); } catch { /* */ }
    if (this.audioCtx.state !== "running") {
      this._rebuildContextSync();
      try { await Promise.race([this.audioCtx.resume(), sleep(1200)]); } catch { /* */ }
    }
  }

  _rebuildContextSync() {
    const old = this.audioCtx;
    this.audioCtx = null;
    this.beatEl = null;
    this.beatSource = null;
    this._ready = false;
    try { old?.close(); } catch { /* */ }
    this._ctx();
    if (this.beatBuffer) this._ready = true;
  }

  async beatPeaks(rate = PEAK_RATE_VIEW) {
    const buf = await this.loadBuffer(this.beatUrl);
    return buf ? { peaks: bufferPeaks(buf, rate), rate, duration_ms: Math.round(buf.duration * 1000) } : null;
  }

  async clipPeaks(url, rate = PEAK_RATE_VIEW) {
    const buf = await this.loadBuffer(url);
    return buf ? { peaks: bufferPeaks(buf, rate), rate, duration_ms: Math.round(buf.duration * 1000) } : null;
  }

  setMaster(preset, intensity) {
    this._masterPreset = preset;
    this._masterIntensity = intensity;
    this.master?.apply(preset, intensity);
  }

  setBeatVolume(v) {
    this._beatVol = Math.max(0, Math.min(1.5, v));
    if (this.beatGain) this.beatGain.gain.setTargetAtTime(this._beatVol, this.audioCtx.currentTime, 0.02);
  }

  setMasterVolume(v) {
    this._masterVol = Math.max(0, Math.min(1.5, v));
    this.master?.setVolume(this._masterVol);
  }

  setMonitorVolume(v) {
    this._monitorVol = Math.max(0, Math.min(1.5, v));
    if (this.monitorGain) this.monitorGain.gain.setTargetAtTime(this._monitorVol, this.audioCtx.currentTime, 0.02);
  }

  // Round-trip estimate for the live monitor (input + output buffers).
  monitorLatencyMs() {
    const ctx = this.audioCtx;
    if (!ctx) return 0;
    const inLat = Number(this.micStream?.getAudioTracks?.()[0]?.getSettings?.().latency || 0);
    return Math.round((Number(ctx.baseLatency || 0) + Number(ctx.outputLatency || 0) + inLat) * 1000);
  }

  setMetronome(cfg) {
    this._metroCfg = { ...this._metroCfg, ...cfg };
    if (!this._playing || this._offline) return;
    if (!this._metroCfg.on) { this._metro?.stop(); return; }
    if (!this._metro) this._metro = new Metronome(this.audioCtx);
    this._metro.start(this._t0, this._fromSec, this._metroCfg.bpm);
  }

  // Live per-lane volume / pan / mute / solo.
  // `fxAllowed` = RGM Studio Pro entitlement (or an active 15 s preview).
  setFxAllowed(on) {
    this._fxAllowed = !!on;
    if (this._mixTracks) this.setTrackMix(this._mixTracks);
  }

  setTrackMix(tracks) {
    this._mixTracks = tracks;
    if (!this.audioCtx) return;
    const anySolo = tracks.some((t) => t.solo);
    for (const t of tracks) {
      let bus = this._buses.get(t.track_id);
      if (!bus) {
        const gain = this.audioCtx.createGain();
        const pan = this.audioCtx.createStereoPanner ? this.audioCtx.createStereoPanner() : null;
        if (pan) pan.connect(this.master.input);
        bus = { gain, pan, fx: null, fxKey: null };
        this._buses.set(t.track_id, bus);
      }
      const fx = this._fxAllowed ? (fxActive(t.fx) ? t.fx : null) : gateOnly(t.fx);
      const key = fx ? JSON.stringify(fx) : "dry";
      if (bus.fxKey !== key) {
        try { bus.gain.disconnect(); } catch { /* */ }
        bus.fx?.dispose();
        bus.fx = buildFxChain(this.audioCtx, fx);
        bus.gain.connect(bus.fx.input);
        bus.fx.output.connect(bus.pan || this.master.input);
        bus.fxKey = key;
      }
      const audible = !t.mute && (!anySolo || t.solo);
      const g = audible ? Math.pow(10, (t.gain_db || 0) / 20) : 0;
      const now = this.audioCtx.currentTime;
      bus.gain.gain.setTargetAtTime(g, now, 0.015);
      if (bus.pan) bus.pan.pan.setTargetAtTime(Math.max(-1, Math.min(1, t.pan || 0)), now, 0.015);
    }
  }

  setClipGain(takeId, db) {
    for (const v of this._voices) {
      if (v.id === takeId && v.clipGain) {
        v.clipGain.gain.setTargetAtTime(Math.pow(10, (db || 0) / 20), this.audioCtx.currentTime, 0.02);
      }
    }
  }

  // Real post-master output level (dBFS) for the meter.
  readOutputLevel() {
    const a = this.master?.analyser;
    if (!a || !this.audioCtx) return null;
    if (!this._meterBuf || this._meterBuf.length !== a.fftSize) this._meterBuf = new Float32Array(a.fftSize);
    a.getFloatTimeDomainData(this._meterBuf);
    let peak = 0;
    let sum = 0;
    for (let i = 0; i < this._meterBuf.length; i++) {
      const v = this._meterBuf[i];
      const av = v < 0 ? -v : v;
      if (av > peak) peak = av;
      sum += v * v;
    }
    const db = (x) => (x > 0 ? 20 * Math.log10(x) : -120);
    return { peakDb: db(peak), rmsDb: db(Math.sqrt(sum / this._meterBuf.length)) };
  }

  positionMs() {
    if (!this._playing || !this.audioCtx) return Math.round(this._posSec * 1000);
    return Math.round((this._fromSec + Math.max(0, this.audioCtx.currentTime - this._t0)) * 1000);
  }

  // Schedule one clip on the shared AudioContext clock.
  _scheduleClip({ id, url, buffer, el, when, sched, gainDb, fin, fout, dest }) {
    const ctx = this.audioCtx;
    const clipGain = ctx.createGain();
    clipGain.gain.value = Math.pow(10, (gainDb || 0) / 20);
    const env = ctx.createGain();
    env.connect(clipGain).connect(dest);
    const at = when + sched.delayMs / 1000;
    for (const e of clipEnvelopeEvents(sched.u0, sched.win, fin, fout)) {
      try {
        if (e.type === "set") env.gain.setValueAtTime(e.v, at + e.t);
        else env.gain.setValueCurveAtTime(Float32Array.from(e.values), at + e.t, Math.max(0.005, e.d));
      } catch { /* overlapping automation on old Safari — envelope skipped */ }
    }
    const voice = { id, env, clipGain };
    if (buffer) {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(env);
      src.start(at, sched.srcOffsetMs / 1000, sched.durMs / 1000);
      voice.src = src;
    } else {
      const media = el || new Audio();
      if (!el) { media.crossOrigin = "anonymous"; media.preload = "auto"; media.src = url; }
      let node = null;
      try { node = ctx.createMediaElementSource(media); node.connect(env); } catch { /* already wired */ }
      voice.el = media; voice.node = node;
      voice.timers = [
        setTimeout(() => {
          try { media.currentTime = sched.srcOffsetMs / 1000; media.play().catch(() => {}); } catch { /* */ }
        }, Math.max(0, (at - ctx.currentTime) * 1000)),
        setTimeout(() => { try { media.pause(); } catch { /* */ } }, Math.max(0, (at - ctx.currentTime) * 1000 + sched.durMs)),
      ];
    }
    this._voices.push(voice);
  }

  _busFor(trackId) {
    if (!this._buses.has(trackId) && this._mixTracks) this.setTrackMix(this._mixTracks);
    return this._buses.get(trackId)?.gain || this.master.input;
  }

  // Start beat + clips from `fromMs` at ctx time `when`.
  async _startAt(fromMs, { takes = [], beatClip, excludeTrackId } = {}, when) {
    const ctx = this.audioCtx;
    let endMs = 0;
    // Never hold the transport hostage to a slow download/decode: clips
    // not decoded within 400 ms stream via a media element this pass.
    const bufs = await Promise.all(takes.map((k) => (this._offline
      ? this.loadBuffer(k.clip_url)
      : Promise.race([this.loadBuffer(k.clip_url), sleep(400).then(() => null)]))));
    if (!this._offline && when < ctx.currentTime + 0.02) when = ctx.currentTime + 0.05;
    const beatDur = this.beatBuffer ? this.beatBuffer.duration * 1000 : (this.beatEl?.duration || 0) * 1000;
    if (beatDur > 0) {
      const bs = Math.max(0, beatClip?.clip_start_ms || 0);
      const be = Math.min(beatDur, beatClip?.clip_end_ms || beatDur);
      endMs = Math.max(endMs, be);
      const sched = clipSchedule(fromMs, bs, bs, be);
      if (sched) {
        this._scheduleClip({
          id: "beat", buffer: this.beatBuffer, el: this.beatEl, when, sched,
          gainDb: beatClip?.clip_gain_db || 0, fin: beatClip?.fade_in_ms, fout: beatClip?.fade_out_ms, dest: this.beatGain,
        });
      }
    }
    takes.forEach((k, i) => {
      if (k.track_id === excludeTrackId) return;
      const cs = k.clip_start_ms || 0;
      const ce = k.clip_end_ms || k.duration_ms || 0;
      const off = k.beat_offset_ms || 0;
      endMs = Math.max(endMs, off + (ce - cs));
      const sched = clipSchedule(fromMs, off, cs, ce);
      if (!sched) return;
      this._scheduleClip({
        id: k.take_id, url: k.clip_url, buffer: bufs[i], when, sched,
        gainDb: k.clip_gain_db, fin: k.fade_in_ms, fout: k.fade_out_ms, dest: this._busFor(k.track_id),
      });
    });
    this._fromSec = fromMs / 1000;
    this._t0 = when;
    this._endSec = endMs / 1000;
    this._playing = true;
    if (!this._offline && this._metroCfg.on) this.setMetronome({});
    return when;
  }

  _stopVoices() {
    for (const v of this._voices) {
      try { v.src?.stop(); } catch { /* */ }
      try { v.src?.disconnect(); } catch { /* */ }
      for (const t of v.timers || []) clearTimeout(t);
      if (v.el) { try { v.el.pause(); } catch { /* */ } if (v.el !== this.beatEl) { try { v.el.src = ""; } catch { /* */ } } }
      try { v.node?.disconnect(); } catch { /* */ }
      try { v.env.disconnect(); v.clipGain.disconnect(); } catch { /* */ }
    }
    this._voices = [];
  }

  _haltTransport() {
    if (this._playing) this._posSec = this.positionMs() / 1000;
    this._playing = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
    this._metro?.stop();
    this._stopVoices();
  }

  // Multi-track playback from the playhead. Sample-accurate: beat and
  // vocals are AudioBufferSourceNodes on one AudioContext clock.
  async playFrom(fromMs, mix = {}, { onTick, onEnd } = {}) {
    await this.ensureReady();
    this._haltTransport();
    await this._startAt(Math.max(0, fromMs || 0), mix, this.audioCtx.currentTime + 0.06);
    const tick = () => {
      if (!this._playing) return;
      const pos = this.positionMs();
      onTick?.(pos / 1000);
      if (this.state === "playing" && pos / 1000 >= this._endSec + 0.05) {
        this._haltTransport();
        this._posSec = 0;
        this._setState("stopped");
        onEnd?.();
        return;
      }
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
    this._setState("playing");
  }

  // Back-compat wrapper.
  async playMix(takes, { onTick, onEnd, beatClip, fromMs = 0 } = {}) {
    return this.playFrom(fromMs, { takes, beatClip }, { onTick, onEnd });
  }

  async pauseMix() {
    this._haltTransport();
    this._setState("stopped");
  }

  async returnToStart() {
    this._haltTransport();
    this._posSec = 0;
    this._setState("ready");
  }

  _teardownMonitor() {
    try { this.micSource?.disconnect(); } catch { /* */ }
    try { this.monitorGain?.disconnect(); } catch { /* */ }
    this.monitorGain = null;
    this.micSource = null;
  }

  _wireMonitor() {
    if (!this.audioCtx || !this.micStream) return false;
    this._teardownMonitor();
    try {
      this.micSource = this.audioCtx.createMediaStreamSource(this.micStream);
      this.monitorGain = this.audioCtx.createGain();
      this.monitorGain.gain.value = this._monitorVol ?? 0.8;
      this.micSource.connect(this.monitorGain).connect(this.audioCtx.destination);
      return true;
    } catch {
      this._teardownMonitor();
      return false;
    }
  }

  async setMonitor(on) {
    this.monitor = !!on;
    if (this.state !== "recording") return;
    if (this.monitor && this.micStream && this.audioCtx) this._wireMonitor();
    else this._teardownMonitor();
  }

  // Legacy low-res peaks (kept for callers outside the session page).
  async peaksForBeatUrl(url, samples = 256) {
    try {
      const buf = await this.loadBuffer(url);
      if (!buf) return { peaks: [], duration_ms: 0 };
      const rate = Math.max(1, samples / buf.duration);
      return { peaks: bufferPeaks(buf, rate).slice(0, samples), duration_ms: Math.round(buf.duration * 1000) };
    } catch { return { peaks: [], duration_ms: 0 }; }
  }

  /**
   * Peak-scan normalization. Returns a `clip_gain_db` recommendation
   * so that the loudest peak lands at `target` (0..255). Non-DSP:
   * only computes a gain factor to store as clip metadata. If peaks
   * are empty or already at/above target, returns 0 dB (no-op).
   */
  static normalizeGainFromPeaks(peaks, target = 220) {
    if (!Array.isArray(peaks) || peaks.length === 0) return 0;
    let mx = 0;
    for (const p of peaks) if (p > mx) mx = p;
    if (mx <= 0 || mx >= target) return 0;
    const ratio = target / mx;
    // 20*log10(ratio), clamped to +12 dB max so a near-silent clip
    // isn't blown into distortion.
    const db = 20 * Math.log10(ratio);
    return Math.max(0, Math.min(12, Math.round(db * 10) / 10));
  }

  static cloudinaryWaveformUrl(url, {
    width = 1024, height = 160, colorHex = "d946ef", bgHex = "00000000",
  } = {}) {
    if (!url || typeof url !== "string") return null;
    const m = url.match(/^(https:\/\/res\.cloudinary\.com\/[^/]+\/video\/upload\/)(?:(v\d+)\/)?(.+?)(\.[a-z0-9]+)?$/i);
    if (!m) return null;
    const [, base, version, publicPath] = m;
    const transform = `fl_waveform,w_${width},h_${height},co_rgb:${colorHex},b_rgb:${bgHex}`;
    return `${base}${transform}/${version ? version + "/" : ""}${publicPath}.png`;
  }

  /**
   * Verify a candidate beat URL is fully decodable AND actually
   * playable through HTMLAudioElement BEFORE we tell the user
   * "Beat uploaded".
   *
   *   • fetch + `decodeAudioData` — catches malformed encoded audio.
   *   • HTMLAudioElement `loadedmetadata` (Safari-safe) — catches
   *     files that decode as ArrayBuffers but the browser media
   *     pipeline can't parse. `canplaythrough` was previously the
   *     pass signal, but iOS Safari deliberately withholds it for
   *     off-screen elements without a user gesture, so a perfectly
   *     playable MP3 timed out and got wrongly rejected. `loadedmetadata`
   *     fires reliably on iOS once the container parses (`readyState
   *     ≥ HAVE_METADATA`) which is the same signal Safari itself
   *     needs before `play()` can succeed.
   *
   * A pass still requires: `decodeAudioData` OK, finite `duration_ms
   * > 0`, `loadedmetadata` observed with `duration > 0 &&
   * isFinite(duration)`, and no `HTMLMediaElement.error`.
   */
  async verifyBeatPlayable(url) {
    try {
      const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
      if (!OfflineCtx) return { ok: false, reason: "no_offline_context" };
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) return { ok: false, reason: `http_${res.status}` };
      const buf = await res.arrayBuffer();
      const ctx = new OfflineCtx(1, 44100, 44100);
      let decoded;
      try {
        decoded = await ctx.decodeAudioData(buf.slice(0));
      } catch (e) {
        return { ok: false, reason: `decode_failed:${e?.message || "unknown"}` };
      }
      const duration_ms = Math.round((decoded.duration || 0) * 1000);
      if (duration_ms <= 0) return { ok: false, reason: "zero_duration" };
      return await this._probeMediaElement(url, duration_ms);
    } catch (e) {
      return { ok: false, reason: e?.message || "decode_failed" };
    }
  }

  /**
   * HTMLAudioElement `loadedmetadata` probe factored out so the
   * regression test can exercise it directly against a fake Audio
   * implementation without needing a real network fetch/decode.
   *
   * Resolves `{ ok, reason }` where `reason` on failure is a
   * compact human-readable diagnostic including MediaError.code,
   * networkState, readyState, currentSrc and which listener caused
   * the failure (`error` | `timeout`).
   */
  async _probeMediaElement(url, decodedDurationMs) {
    const el = new Audio();
    el.crossOrigin = "anonymous";
    el.preload = "auto";
    el.src = url;
    const diag = () => {
      const e = el.error;
      const parts = [];
      if (e) {
        if (typeof e.code !== "undefined") parts.push(`code=${e.code}`);
        if (e.message) parts.push(`msg=${e.message}`);
      }
      parts.push(`net=${el.networkState}`);
      parts.push(`ready=${el.readyState}`);
      if (el.currentSrc) {
        // Keep the tail so the artist can spot the file id without
        // the URL blowing out the toast.
        parts.push(`src=…${String(el.currentSrc).slice(-40)}`);
      }
      return parts.join(" ");
    };
    const result = await new Promise((resolve) => {
      const to = setTimeout(() => resolve({ ok: false, resolvedBy: "timeout" }), 12000);
      el.addEventListener("loadedmetadata", () => {
        const d = el.duration;
        clearTimeout(to);
        if (el.error) {
          resolve({ ok: false, resolvedBy: "error" });
          return;
        }
        if (!isFinite(d) || d <= 0) {
          resolve({ ok: false, resolvedBy: "invalid_duration" });
          return;
        }
        resolve({ ok: true, duration_ms: Math.round(d * 1000) });
      }, { once: true });
      el.addEventListener("error", () => { clearTimeout(to); resolve({ ok: false, resolvedBy: "error" }); }, { once: true });
    });
    // Take the diagnostic snapshot BEFORE we detach the src so
    // networkState/readyState/currentSrc still reflect the failure
    // moment.
    const snapshot = result.ok ? "" : diag();
    try { el.src = ""; } catch { /* */ }
    if (!result.ok) {
      return { ok: false, reason: `media_${result.resolvedBy}:${snapshot}` };
    }
    return { ok: true, duration_ms: result.duration_ms || decodedDurationMs };
  }


  async stopBeat() {
    this._haltTransport();
    this._posSec = 0;
    this._setState("ready");
  }

  // 3-2-1 countdown → recorder starts → beat + other vocals are
  // scheduled on the AudioContext clock from `startAtMs` (punch-in).
  // The recorder pre-roll and output/input latency are measured and
  // returned as `lead_in_ms` so the take lands exactly on the beat.
  async startRecording({ mime, onCountdown, onError, onTick, onLevel, monitor, startAtMs = 0, mix = {} } = {}) {
    if (this.state === "recording") return;
    await this.ensureReady();
    if (!this.audioCtx) throw new Error("engine not ready");
    this._haltTransport();
    setAudioSession("play-and-record");
    if (typeof monitor === "boolean") this.monitor = monitor;
    if (!this.micStream) this.micStream = await openMic(this.monitor);

    if (onCountdown) {
      for (const n of [3, 2, 1]) { onCountdown(n); await sleep(1000); }
      onCountdown(0);
    }
    this._wakeLock = await requestWakeLock();
    const ctx = this.audioCtx;
    if (ctx.state === "suspended") { try { await ctx.resume(); } catch { /* */ } }

    let analyser = null;
    let levelBuf = null;
    try {
      this._levelSource = ctx.createMediaStreamSource(this.micStream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      levelBuf = new Uint8Array(analyser.fftSize);
      this._levelSource.connect(analyser); // observe only — never audible
    } catch { /* level meter is best-effort */ }
    this._levelAnalyser = analyser;

    this.recordedChunks = [];
    try {
      this.recorder = new MediaRecorder(this.micStream, mime ? { mimeType: mime } : undefined);
    } catch (e) {
      this._teardownLevelMeter();
      onError?.(e);
      throw e;
    }
    const rec = this.recorder;
    rec.ondataavailable = (evt) => { if (evt.data && evt.data.size > 0) this.recordedChunks.push(evt.data); };
    rec.onerror = (evt) => onError?.(evt.error || new Error("recorder error"));
    const started = new Promise((res) => {
      rec.onstart = () => res(ctx.currentTime);
      setTimeout(() => res(null), 1000);
    });
    rec.start(1000);
    const r0 = (await started) ?? ctx.currentTime;
    this._recStartCtx = r0;

    const startMs = Math.max(0, Math.round(startAtMs || 0));
    this._recStartAtMs = startMs;
    this._startedAtBeat = startMs / 1000;
    const t0 = await this._startAt(startMs, mix, ctx.currentTime + 0.12);
    const outLat = Number(ctx.outputLatency || ctx.baseLatency || 0);
    const inLat = Number(this.micStream.getAudioTracks?.()[0]?.getSettings?.().latency || 0);
    this._latencySec = Math.min(0.5, Math.max(0, outLat + inLat));
    this._leadInSec = Math.max(0, t0 - r0) + this._latencySec;
    if (this.monitor) this._wireMonitor();

    const tick = () => {
      if (this.state !== "recording") return;
      const pos = this.positionMs();
      onTick?.(pos / 1000);
      if (analyser && levelBuf && onLevel) {
        analyser.getByteTimeDomainData(levelBuf);
        let peak = 0;
        for (let i = 0; i < levelBuf.length; i++) {
          const v = Math.abs(levelBuf[i] - 128);
          if (v > peak) peak = v;
        }
        onLevel(Math.min(255, peak * 2), pos);
      }
      this._raf = requestAnimationFrame(tick);
    };
    this._setState("recording");
    this._raf = requestAnimationFrame(tick);
  }

  _teardownLevelMeter() {
    try { this._levelAnalyser?.disconnect(); } catch { /* */ }
    try { this._levelSource?.disconnect(); } catch { /* */ }
    this._levelAnalyser = null;
    this._levelSource = null;
  }

  _releaseMic() {
    try { this.micStream?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
    this.micStream = null;
  }

  async stopRecording() {
    if (this.state !== "recording" || !this.recorder) return null;
    const ctx = this.audioCtx;
    const stopCtx = ctx.currentTime;
    const rec = this.recorder;
    const stopped = new Promise((res) => { rec.onstop = res; });
    try { rec.stop(); } catch { /* */ }
    await stopped;
    this._haltTransport();
    this._posSec = this._recStartAtMs / 1000 + Math.max(0, stopCtx - this._t0);
    try { this._wakeLock?.release?.(); } catch { /* */ }
    this._wakeLock = null;
    this._teardownMonitor();
    this._teardownLevelMeter();
    this._releaseMic();
    setAudioSession("playback");
    const mime = rec.mimeType || "audio/webm";
    const blob = new Blob(this.recordedChunks, { type: mime });
    const durationMs = Math.max(0, Math.round((stopCtx - this._recStartCtx) * 1000))
      || recorderClockMs(ctx, this._recStartCtx, this._recStartCtx);
    const timing = {
      duration_ms: durationMs,
      beat_offset_ms: this._recStartAtMs,
      lead_in_ms: Math.min(Math.round(this._leadInSec * 1000), Math.max(0, durationMs - 1)),
      latency_ms: Math.round((this._latencySec || 0) * 1000),
      ctx_start_ms: Math.round(this._recStartCtx * 1000),
    };
    this.recordedChunks = [];
    this.recorder = null;
    // Fresh context is created on the next Play/Record tap (in-gesture).
    if (isIOS()) this._needsRebuild = true;
    this._setState("stopped");
    return { blob, mime, timing };
  }

  // Decode a fresh take → stored peaks (20/s) + view peaks (100/s).
  async peakify(blob) {
    try {
      const buffer = await decodeAudio(this._ctx(), await blob.arrayBuffer());
      return {
        buffer,
        peaks: bufferPeaks(buffer, PEAK_RATE_STORE),
        viewPeaks: bufferPeaks(buffer, PEAK_RATE_VIEW),
        duration_ms: Math.round(buffer.duration * 1000),
      };
    } catch {
      return null;
    }
  }

  dispose() {
    try { this.recorder?.stop(); } catch { /* */ }
    try { this._haltTransport(); } catch { /* */ }
    this._releaseMic();
    try { this._teardownMonitor(); } catch { /* */ }
    try { this._teardownLevelMeter(); } catch { /* */ }
    try { this.audioCtx?.close(); } catch { /* */ }
    setAudioSession("auto");
    this.audioCtx = null;
    this.beatEl = null;
    this._buffers = new Map();
  }
}

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }
